"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useI18n } from "@/i18n"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { LoadingState, ErrorState } from "@/components/state"
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress"
import { OnboardingStepShell } from "@/components/onboarding/OnboardingStepShell"
import { BlockerScreen } from "@/components/onboarding/BlockerScreen"
import { FinishingScreen } from "@/components/onboarding/FinishingScreen"
import { useOnboardingFlow } from "@/components/onboarding/useOnboardingFlow"
import { trackFunnelEvent } from "@/lib/analytics"
import { calorieFloor } from "@/components/onboarding/calorieTarget"
import type { CalorieTargetDraft } from "@/components/onboarding/types"
import type { PantryCandidate } from "@bitebalance/contracts"

import { Step1Language } from "@/components/onboarding/steps/Step1Language"
import { Step2Eligibility } from "@/components/onboarding/steps/Step2Eligibility"
import { Step3Goal } from "@/components/onboarding/steps/Step3Goal"
import { Step4Body } from "@/components/onboarding/steps/Step4Body"
import { Step5Dietary } from "@/components/onboarding/steps/Step5Dietary"
import { Step6Allergens } from "@/components/onboarding/steps/Step6Allergens"
import { Step13HealthConditions } from "@/components/onboarding/steps/Step13HealthConditions"
import { Step7Taste } from "@/components/onboarding/steps/Step7Taste"
import { Step8Cooking } from "@/components/onboarding/steps/Step8Cooking"
import { Step9Time } from "@/components/onboarding/steps/Step9Time"
import { Step10CookingStyle } from "@/components/onboarding/steps/Step10CookingStyle"
import { Step11Budget } from "@/components/onboarding/steps/Step11Budget"
import { Step12PantryReview } from "@/components/onboarding/steps/Step12PantryReview"
import { HealthConsentScreen } from "@/components/consent/HealthConsentScreen"

const EYEBROW_TITLE_BODY: Record<number, [string, string, string]> = {
  1: ["onboarding.step1.eyebrow", "onboarding.step1.title", "onboarding.step1.body"],
  2: ["onboarding.step2.eyebrow", "onboarding.step2.title", "onboarding.step2.body"],
  3: ["onboarding.step3.eyebrow", "onboarding.step3.title", "onboarding.step3.body"],
  4: ["onboarding.step4.eyebrow", "onboarding.step4.title", "onboarding.step4.body"],
  5: ["onboarding.step5.eyebrow", "onboarding.step5.title", "onboarding.step5.body"],
  6: ["onboarding.step6.eyebrow", "onboarding.step6.title", "onboarding.step6.body"],
  13: ["onboarding.step13.eyebrow", "onboarding.step13.title", "onboarding.step13.body"],
  7: ["onboarding.step7.eyebrow", "onboarding.step7.title", "onboarding.step7.body"],
  8: ["onboarding.step8.eyebrow", "onboarding.step8.title", "onboarding.step8.body"],
  9: ["onboarding.step9.eyebrow", "onboarding.step9.title", "onboarding.step9.body"],
  10: ["onboarding.step10.eyebrow", "onboarding.step10.title", "onboarding.step10.body"],
  11: ["onboarding.step11.eyebrow", "onboarding.step11.title", "onboarding.step11.body"],
  12: ["onboarding.step12.eyebrow", "onboarding.step12.title", "onboarding.step12.body"],
}

/**
 * /app/onboarding — the T11 mandatory 13-wire-step flow (visually 13 steps
 * too; step 13's health-condition wire number is unchanged but displayed
 * right after allergens — see components/onboarding/steps.ts). Full-page
 * (no AppShell chrome) so the flow can be deep-linked/resumed and never
 * competes with bottom-nav/left-rail affordances mid-onboarding.
 */
export default function OnboardingPage() {
  const { t, locale } = useI18n()
  const flow = useOnboardingFlow()
  const reduce = useReducedMotion()
  const [pendingCalorieTarget, setPendingCalorieTarget] = useState<CalorieTargetDraft | null>(null)

  // G04: fires once per mount of the onboarding UI — a resumed session
  // (reload mid-flow) fires it again, which is the correct funnel read
  // (the visitor is starting a new onboarding *session*, not a new
  // account); onboarding_completed below is the de-dupe-worthy signal.
  useEffect(() => {
    trackFunnelEvent("onboarding_started")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Direction-aware screen transitions (owner reference video 2026-08-29):
  // forward answers slide in from the right, going back slides in from the
  // left — the flow reads as one continuous journey, not page reloads.
  const prevPositionRef = useRef(flow.visualPositionNumber)
  const direction = flow.visualPositionNumber >= prevPositionRef.current ? 1 : -1
  useEffect(() => {
    prevPositionRef.current = flow.visualPositionNumber
  }, [flow.visualPositionNumber])

  // Tap-to-advance (pure single-select screens): give the selection beat a
  // moment to land visually, then continue. goNext goes through a ref so the
  // delayed call runs the POST-selection closure — the selection-time one
  // still holds the old draft answers and would fail validation silently.
  const goNextRef = useRef(flow.goNext)
  goNextRef.current = flow.goNext
  const currentStepRef = useRef(flow.currentStep)
  currentStepRef.current = flow.currentStep
  const autoAdvanceRef = useRef<number | null>(null)
  const handleAutoAdvance = useCallback(() => {
    if (autoAdvanceRef.current) window.clearTimeout(autoAdvanceRef.current)
    const scheduledStep = currentStepRef.current
    autoAdvanceRef.current = window.setTimeout(() => {
      // Fire only if we're still ON the step the tap happened on — a user
      // (or test) that already moved must never get double-advanced.
      if (currentStepRef.current === scheduledStep) void goNextRef.current()
    }, 280)
  }, [])
  useEffect(
    () => () => {
      if (autoAdvanceRef.current) window.clearTimeout(autoAdvanceRef.current)
    },
    []
  )

  /** Screens that are one tap = one answer — no Continue button (see StepBody). */
  const isTapToAdvanceStep = flow.currentStep === 5

  const handleContinue = useCallback(async () => {
    if (flow.currentStep === 4) {
      const bmrInputs = {
        sex: (flow.draftAnswers[4]?.sex as "female" | "male" | null | undefined) ?? null,
        age: (flow.draftAnswers[4]?.age as number | null | undefined) ?? null,
        heightCm: (flow.draftAnswers[4]?.heightCm as number | null | undefined) ?? null,
        weightKg: (flow.draftAnswers[4]?.weightKg as number | null | undefined) ?? null,
      }
      if (pendingCalorieTarget) {
        const floor = calorieFloor(bmrInputs)
        const ok = flow.proposeCalorieTarget(pendingCalorieTarget, floor)
        if (!ok) return
      }
    }

    if (flow.currentStep === 13) {
      const conditions = (flow.draftAnswers[13]?.conditions as string[] | undefined) ?? []
      if (conditions.includes("eating_disorder_history")) {
        flow.setIneligible("ineligible_ed_history")
        return
      }
      if (flow.checkPregnancyGoalConflict(conditions)) return
    }

    await flow.goNext()
  }, [flow, pendingCalorieTarget])

  const handleUseFloor = useCallback(
    (source: "calculated" | "manual") => {
      if (flow.blocker?.kind !== "below_floor_target") return
      const accepted: CalorieTargetDraft = {
        kcal: flow.blocker.floorKcal,
        source,
        floorAcknowledged: true,
      }
      flow.acceptFloorTarget(flow.blocker.floorKcal, source)
      setPendingCalorieTarget(accepted)
      void flow.goNext()
    },
    [flow]
  )

  const handleJumpToGoal = useCallback(() => {
    flow.clearBlocker()
    flow.jumpToStep(3)
  }, [flow])

  // ADR-030 (Option B): breastfeeding + lose_weight is a non-blocking
  // acknowledgement gate, not a hard block — clearing it proceeds straight
  // to the next step rather than re-running handleContinue (which would
  // immediately re-raise the same gate).
  const handleAcknowledgeBreastfeedingLoss = useCallback(() => {
    flow.clearBlocker()
    void flow.goNext()
  }, [flow])

  const handleConfirmCandidates = useCallback(
    async (payload: Array<{ candidate: PantryCandidate; include: boolean; quantity: number }>) => {
      const candidates = payload
        .filter(p => p.include)
        .map(p => ({
          candidateId: p.candidate.candidateId,
          include: true,
          canonicalIngredientId: p.candidate.canonicalIngredientId,
          normalizedQuantity: p.quantity,
          normalizedUnit: p.candidate.normalizedUnit,
          storageLocation: null,
          expiryDate: null,
        }))
      if (candidates.length === 0) return
      await flow.confirmPantryCandidates(candidates)
    },
    [flow]
  )

  if (flow.loadState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <LoadingState />
      </div>
    )
  }

  if (flow.loadState === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-4">
        <ErrorState onRetry={() => window.location.reload()} />
      </div>
    )
  }

  // Explicit health/AI consent: its own screen between the first step (18+, name, date of birth) and the
  // first step that sends health data. Back returns to step 2; the progress bar does not advance.
  if (flow.needsHealthConsent) {
    return (
      <div className="flex min-h-screen flex-col gap-8 bg-cream px-4 py-10">
        <div className="mx-auto flex w-full max-w-2xl justify-end">
          <LanguageToggle />
        </div>
        <OnboardingProgress
          current={1}
          total={flow.totalSteps}
          onBack={() => flow.jumpToStep(2)}
          canGoBack
        />
        <HealthConsentScreen onConsented={flow.recordHealthConsent} />
      </div>
    )
  }

  if (flow.blocker) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream px-4 py-10">
        <BlockerScreen
          blocker={flow.blocker}
          onDismiss={flow.clearBlocker}
          onJumpToGoal={handleJumpToGoal}
          onUseFloor={handleUseFloor}
          onAcknowledgeBreastfeedingLoss={handleAcknowledgeBreastfeedingLoss}
        />
      </div>
    )
  }

  // Item 35: takes over the whole page from step 12's submit until the plan
  // job resolves (or a generous timeout/error is hit) — never a silent
  // hard-navigate to an unrelated page with its own separate loading flash.
  if (flow.finishPhase !== "idle") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream px-4 py-10">
        <FinishingScreen
          phase={flow.finishPhase}
          errorDetail={flow.finishErrorDetail}
          onRetry={() => void flow.finishOnboarding()}
          onContinueAnyway={flow.continueAnyway}
        />
      </div>
    )
  }

  const [eyebrowKey, titleKey, bodyKey] = EYEBROW_TITLE_BODY[flow.currentStep]

  return (
    <div className="flex min-h-screen flex-col gap-8 bg-cream px-4 py-10">
      <div className="mx-auto flex w-full max-w-2xl justify-end">
        <LanguageToggle />
      </div>
      <OnboardingProgress
        current={flow.visualPositionNumber}
        total={flow.totalSteps}
        onBack={flow.goBack}
        canGoBack={!flow.isFirstStep && !flow.saving}
      />
      <AnimatePresence mode="wait" initial={false} custom={direction}>
        <motion.div
          key={flow.currentStep}
          custom={direction}
          initial={reduce ? false : { opacity: 0, x: 44 * direction }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? undefined : { opacity: 0, x: -36 * direction }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          <OnboardingStepShell
            eyebrow={t(eyebrowKey)}
            title={t(titleKey)}
            body={t(bodyKey)}
            onContinue={() => void (flow.isLastStep ? flow.finishOnboarding() : handleContinue())}
            canContinue={flow.currentStepValid}
            isLastStep={flow.isLastStep}
            saving={flow.saving}
            saveError={flow.saveError}
            continueHidden={isTapToAdvanceStep}
            continueLabelOverride={
              flow.isLastStep ? t("onboarding.step12.createPlanButton") : undefined
            }
            footerExtra={
              flow.isLastStep ? (
                <p className="max-w-[16rem] text-right text-xs text-ink-2">
                  {t("onboarding.shell.tuneLaterHint")}
                </p>
              ) : undefined
            }
          >
            <StepBody
              step={flow.currentStep}
              answer={flow.currentAnswer}
              onChange={flow.setCurrentAnswer}
              locale={locale}
              flow={flow}
              onIneligibleAge={() => flow.setIneligible("ineligible_age")}
              onConfirmCandidates={handleConfirmCandidates}
              onPendingCalorieTarget={setPendingCalorieTarget}
              onAutoAdvance={handleAutoAdvance}
            />
          </OnboardingStepShell>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

interface StepBodyProps {
  step: number
  answer: Record<string, unknown>
  onChange: (
    patch: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)
  ) => void
  locale: "de" | "en"
  flow: ReturnType<typeof useOnboardingFlow>
  onIneligibleAge: () => void
  onConfirmCandidates: (
    payload: Array<{ candidate: PantryCandidate; include: boolean; quantity: number }>
  ) => Promise<void>
  onPendingCalorieTarget: (draft: CalorieTargetDraft | null) => void
  /** Tap-to-advance: pure single-select steps call this right after onChange. */
  onAutoAdvance: () => void
}

function StepBody({
  step,
  answer,
  onChange,
  locale,
  flow,
  onIneligibleAge,
  onConfirmCandidates,
  onPendingCalorieTarget,
  onAutoAdvance,
}: StepBodyProps) {
  switch (step) {
    case 1:
      return <Step1Language value={answer} onChange={onChange} />
    case 2:
      return <Step2Eligibility value={answer} onChange={onChange} onIneligible={onIneligibleAge} />
    case 3:
      return <Step3Goal value={answer} onChange={onChange} />
    case 4: {
      const goalType =
        (flow.draftAnswers[3]?.goalType as
          "maintain" | "loss" | "gain" | "improve_health" | undefined) ?? null
      return (
        <Step4Body
          value={answer}
          onChange={onChange}
          goalType={goalType}
          initialCalorieTarget={flow.calorieTarget}
          onTargetDraftChange={onPendingCalorieTarget}
        />
      )
    }
    case 5:
      return <Step5Dietary value={answer} onChange={onChange} onSelected={onAutoAdvance} />
    case 6:
      return <Step6Allergens value={answer} onChange={onChange} />
    case 13:
      return <Step13HealthConditions value={answer} onChange={onChange} />
    case 7:
      return <Step7Taste value={answer} onChange={onChange} />
    case 8:
      return <Step8Cooking value={answer} onChange={onChange} />
    case 9:
      return <Step9Time value={answer} onChange={onChange} />
    case 10:
      return <Step10CookingStyle value={answer} onChange={onChange} />
    case 11:
      return <Step11Budget value={answer} onChange={onChange} />
    case 12:
      return (
        <Step12PantryReview
          value={answer}
          onChange={onChange}
          allAnswers={flow.draftAnswers}
          pantryExtraction={flow.pantryExtraction}
          onExtract={async text => {
            await flow.extractPantryText(text, locale)
          }}
          onConfirmCandidates={onConfirmCandidates}
          onClearExtraction={flow.clearPantryExtraction}
        />
      )
    default:
      return null
  }
}
